import { apiRequest } from "./api";

export type AffiliateType = "club" | "coach" | "parent" | "athlete";
export type RewardKind = "money" | "plan_grant" | "lives";
export type Recurrence = "one_time" | "monthly";

export interface RewardCell {
  id: string;
  affiliate_type: AffiliateType;
  plan_code: string;
  reward_kind: RewardKind;
  amount_cents: number | null;
  grant_plan_code: string | null;
  grant_months: number | null;
  lives: number | null;
  recurrence: Recurrence;
  is_active: boolean;
}

export interface RewardCellPayload {
  affiliate_type: AffiliateType;
  plan_code: string;
  reward_kind: RewardKind;
  amount_cents?: number;
  grant_plan_code?: string | null;
  grant_months?: number | null;
  lives?: number | null;
  recurrence?: Recurrence;
  is_active?: boolean;
}

export async function getRewardConfig(): Promise<RewardCell[]> {
  const data = await apiRequest<{ config: RewardCell[] }>("/admin/affiliate/config");
  return data.config;
}

export async function upsertRewardCell(payload: RewardCellPayload): Promise<RewardCell> {
  const data = await apiRequest<{ cell: RewardCell }>("/admin/affiliate/config", { method: "PUT", data: payload });
  return data.cell;
}

export async function deleteRewardCell(id: string): Promise<void> {
  await apiRequest(`/admin/affiliate/config/${id}`, { method: "DELETE" });
}

// --- Affiliate "my code" (any role) ---
export interface MyAffiliate {
  personal: { type: AffiliateType; code: string; link: string } | null;
  club: { id: string; code: string; link: string; balance_cents: number } | null;
  referrals: { total: number; converted: number };
}

export async function getMyAffiliate(): Promise<MyAffiliate> {
  return apiRequest<MyAffiliate>("/affiliate/me");
}

export interface LedgerRow {
  id: string;
  kind: string;
  amount_cents: number | null;
  meta: any;
  period: string | null;
  status: string;
  created_at: string;
}
export interface PayoutRow {
  id: string;
  amount_cents: number;
  status: string;
  note: string | null;
  created_at: string;
  processed_at: string | null;
}
export interface ReferralRow {
  id: string;
  name: string;
  status: "pending" | "converted" | "reversed";
  /** The plan the commission was earned on — history, not the present. */
  plan_code: string | null;
  /** What the athlete is on today; "free" when nobody pays for them. */
  current_plan_code?: string;
  team?: { id: string; name: string } | null;
  coach?: { id: string; name: string | null } | null;
  created_at: string;
  converted_at: string | null;
}
export interface ClubAffiliateSummary {
  balance_cents: number;
  ledger: LedgerRow[];
  payouts: PayoutRow[];
  referrals: ReferralRow[];
  stats: {
    referrals_total: number;
    referrals_converted: number;
    invitations_sent: number;
    invitations_accepted: number;
  };
}

export async function getClubAffiliateSummary(clubId: string): Promise<ClubAffiliateSummary> {
  return apiRequest<ClubAffiliateSummary>(`/affiliate/club/${clubId}/summary`);
}

export async function requestPayout(clubId: string, amountCents: number, note?: string): Promise<void> {
  await apiRequest(`/affiliate/club/${clubId}/payout`, { method: "POST", data: { amount_cents: amountCents, note } });
}

// --- Admin payout management ---
export interface AdminPayout {
  id: string;
  club_id: string;
  amount_cents: number;
  status: string;
  note: string | null;
  created_at: string;
  processed_at: string | null;
}

export async function getAdminPayouts(status?: string): Promise<AdminPayout[]> {
  const data = await apiRequest<{ payouts: AdminPayout[] }>("/admin/affiliate/payouts", {
    params: status ? { status } : undefined,
  });
  return data.payouts;
}

export async function updatePayoutStatus(id: string, status: "approved" | "paid" | "rejected"): Promise<void> {
  await apiRequest(`/admin/affiliate/payouts/${id}`, { method: "PATCH", data: { status } });
}
